// US-072: read-only fetch of product custom-column values for the Create-PO
// table. Values are keyed variant_id -> columnId -> string and are stored in
// ProductCustomColumns (shared across all POs) — the same source PO detail
// reads. This screen never writes them.
//
// The HTTP getter is injected (defaulting to the real http.get) so the wrapper
// unit-tests without chain-loading SupplierModel / models/report.ts.

import { get as httpGet } from '@/services/http';

const ENDPOINT = '/purchase_orders/config/custom-column-values';
const BATCH_SIZE = 100;

export type CustomColumnValuesByVariant = Record<string, Record<string, string>>;

interface Deps {
  get: (url: string, params?: any) => Promise<any>;
}

/**
 * Fetch custom-column values for the given variant ids, batched in chunks of
 * 100. Returns a variant_id -> { columnId: value } map. Empty/duplicate ids are
 * handled; a chunk with no `data` contributes nothing.
 */
export async function fetchCustomColumnValuesByVariant(
  variantIds: Array<number | string>,
  deps: Deps = { get: httpGet },
): Promise<CustomColumnValuesByVariant> {
  const result: CustomColumnValuesByVariant = {};
  if (!Array.isArray(variantIds) || variantIds.length === 0) {
    return result;
  }

  const unique = Array.from(new Set(
    variantIds.filter((id) => id !== null && id !== undefined && id !== '').map(String),
  ));
  if (unique.length === 0) {
    return result;
  }

  // Build the chunks then dispatch them concurrently — a "show all" page can be
  // ~100 chunks; awaiting each sequentially would serialize that many round trips.
  const chunks: string[][] = [];
  for (let i = 0; i < unique.length; i += BATCH_SIZE) {
    chunks.push(unique.slice(i, i + BATCH_SIZE));
  }

  const responses = await Promise.all(
    chunks.map((chunk) => deps.get(ENDPOINT, {
      object_ids: chunk.join(','),
      object_type: 'variant',
    })),
  );

  for (const response of responses) {
    const data = response?.data || {};
    for (const [variantId, values] of Object.entries(data)) {
      result[variantId] = values as Record<string, string>;
    }
  }

  return result;
}
